import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Rating,
  Chip,
  Skeleton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CheckCircleOutline,
  Hotel,
  DirectionsBus,
  Restaurant,
} from '@mui/icons-material';
import { HeaderV2, FooterV2 } from '../../components/layout';
import axios from 'axios';
import { getDemoPackageById } from '../../data/demoPackages';
import { colors } from '../../components/theme/colors';
import { API_BASE_URL as API_BASE } from '../../config/api';

const PackageDetailsPageV2 = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);

      // Demo IDs resolve instantly (no API required)
      const demo = getDemoPackageById(id);
      if (demo) {
        if (mounted) {
          setPkg(demo);
          setLoading(false);
        }
        return;
      }

      try {
        const res = await axios.get(`${API_BASE}/packages/${id}`);
        const data = res.data?.data || res.data;
        if (mounted) {
          if (data) setPkg(data);
          else setError('Package not found');
        }
      } catch (e) {
        const fallback = getDemoPackageById(id);
        if (mounted) {
          if (fallback) setPkg(fallback);
          else setError('Failed to load package details');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (id) load();
    return () => {
      mounted = false;
    };
  }, [id]);

  const image =
    pkg?.packageImages?.[0]?.imageUrl ||
    pkg?.packageImage ||
    pkg?.image ||
    'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1200&q=70';

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
      <HeaderV2 />
      <Container maxWidth="lg" sx={{ py: { xs: 10, md: 12 } }}>
        {loading ? (
          <Skeleton variant="rectangular" height={360} sx={{ borderRadius: 2 }} />
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button variant="contained" color="secondary" onClick={() => navigate('/v2/packages')}>
              Back to trips
            </Button>
          </Box>
        ) : !pkg ? (
          <Typography>Package not found</Typography>
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: '#fff',
                  border: `1px solid ${colors.border.light}`,
                }}
              >
                <Box
                  component="img"
                  src={image}
                  alt={pkg.name || pkg.title}
                  sx={{ width: '100%', height: { xs: 240, md: 380 }, objectFit: 'cover', display: 'block' }}
                />
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {pkg.category && (
                      <Chip label={pkg.category} size="small" sx={{ bgcolor: 'rgba(31,111,120,0.12)' }} />
                    )}
                    {!!pkg.nightsInMakkah && <Chip label={`Makkah ${pkg.nightsInMakkah}N`} size="small" />}
                    {!!pkg.nightsInMadina && <Chip label={`Madina ${pkg.nightsInMadina}N`} size="small" />}
                    {pkg.mealPlan && <Chip label={`Meals: ${pkg.mealPlan}`} size="small" />}
                    {pkg.destinationLabel && <Chip label={pkg.destinationLabel} size="small" />}
                  </Box>

                  {pkg.highlights?.length > 0 && (
                    <>
                      <Typography sx={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600, mb: 1 }}>
                        Highlights
                      </Typography>
                      <List dense disablePadding sx={{ mb: 2 }}>
                        {pkg.highlights.map((h) => (
                          <ListItem key={h} disableGutters>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleOutline sx={{ color: colors.secondary.main, fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText primary={h} />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}

                  {pkg.includes?.length > 0 && (
                    <>
                      <Typography sx={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600, mb: 1 }}>
                        What&apos;s included
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {pkg.includes.map((item) => (
                          <Chip key={item} label={item} variant="outlined" size="small" />
                        ))}
                      </Box>
                    </>
                  )}

                  {(pkg.hotels?.length > 0 || pkg.transport) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Grid container spacing={2}>
                        {pkg.hotels?.length > 0 && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                              <Hotel fontSize="small" sx={{ color: colors.secondary.main }} />
                              <Typography fontWeight={600}>Hotels</Typography>
                            </Box>
                            {pkg.hotels.map((h) => (
                              <Typography key={`${h.city}-${h.name}`} variant="body2" color="text.secondary">
                                {h.city}: {h.name}
                                {h.stars ? ` (${h.stars}★)` : ''}
                              </Typography>
                            ))}
                          </Grid>
                        )}
                        {pkg.transport && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                              <DirectionsBus fontSize="small" sx={{ color: colors.secondary.main }} />
                              <Typography fontWeight={600}>Transport</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {pkg.transport}
                            </Typography>
                            {pkg.mealPlan && (
                              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
                                <Restaurant fontSize="small" sx={{ color: colors.secondary.main }} />
                                <Typography variant="body2" color="text.secondary">
                                  Meal plan: {pkg.mealPlan}
                                </Typography>
                              </Box>
                            )}
                          </Grid>
                        )}
                      </Grid>
                    </>
                  )}

                  {pkg.itinerary?.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography sx={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600, mb: 1 }}>
                        Sample itinerary
                      </Typography>
                      {pkg.itinerary.map((day) => (
                        <Box key={`${day.day}-${day.title}`} sx={{ mb: 1.5 }}>
                          <Typography fontWeight={600} variant="body2">
                            Day {day.day}: {day.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {day.description}
                          </Typography>
                        </Box>
                      ))}
                    </>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: '#fff',
                  border: `1px solid ${colors.border.light}`,
                  position: { md: 'sticky' },
                  top: { md: 96 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontWeight: 600,
                    fontSize: '1.75rem',
                    mb: 1,
                    lineHeight: 1.25,
                  }}
                >
                  {pkg.name || pkg.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Rating value={pkg.rating || pkg.averageRating || 4.5} precision={0.5} readOnly size="small" />
                  <Typography variant="body2" color="text.secondary">
                    {pkg.duration || 0} days
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                  from {(
                    pkg.currency === 'PKR' ||
                    pkg.category === 'Pakistan' ||
                    pkg.category === 'Local' ||
                    /pakistan|swat|hunza|skardu|murree|lahore|naran/i.test((pkg.name || pkg.title || '') + ' ' + (pkg.destinationLabel || ''))
                  )
                    ? `PKR ${Number(pkg.basePrice || pkg.price || 0).toLocaleString()}`
                    : `$${Number(pkg.basePrice || pkg.price || 0).toLocaleString()}`}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {pkg.description || pkg.summary}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    onClick={() => navigate(`/v2/booking/${pkg.id}`)}
                  >
                    Book this trip
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={() => navigate('/v2/customized-booking')}
                  >
                    Customize my trip
                  </Button>
                  <Button variant="text" onClick={() => navigate('/v2/packages')}>
                    Back to all trips
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Container>
      <FooterV2 />
    </Box>
  );
};

export default PackageDetailsPageV2;
